import { isEmpty } from "lodash-es";
import { Alert, Button, DeleteIcon, Space } from "melco-ui";
import { useTranslation } from "react-i18next";
import { Key } from "antd/lib/table/interface";
import {
  ControlcenterMelcoModelsSupplier,
  useConfirm,
} from "melco-shared-logic";
import { useShowWarningForRowKeys } from "../../hooks/table/useShowWarningForRowKeys";
import { useDeleteSuppliers } from "../../hooks/suppliers/useDeleteSuppliers";

type BulkActionDeleteSuppliersProps<T> = {
  selectedRowKeys: Key[];
  setSelectedRowKeys: (keys: Key[]) => void;
  refetch?: () => Promise<any>;
  data?: T[];
};

export const BulkActionDeleteSuppliers: React.FC<
  BulkActionDeleteSuppliersProps<ControlcenterMelcoModelsSupplier>
> = ({ selectedRowKeys, setSelectedRowKeys, refetch, data }) => {
  const { t } = useTranslation();

  const deleteSuppliers = useDeleteSuppliers();
  const showWarningForRowKeys = useShowWarningForRowKeys(selectedRowKeys, data);

  const confirmDelete = useConfirm(
    async () => {
      const successfullyDeletedIds = await deleteSuppliers(
        selectedRowKeys.map((key) => key.toString())
      );

      if (refetch) {
        refetch();
      }

      // keep items that were not deleted selected
      setSelectedRowKeys(
        selectedRowKeys.filter(
          (id) => !successfullyDeletedIds.includes(id.toString())
        )
      );
    },
    {
      translationPrefix: "managesuppliers.actions.delete",
      translationOptions: {
        count: selectedRowKeys.length,
      },
      content: showWarningForRowKeys.length && (
        <Space direction="vertical" size="small">
          {t("managesuppliers.actions.delete.confirmation.message", {
            count: selectedRowKeys.length,
          })}
          <Alert
            type="warning"
            withBackground
            description={t(
              "managesuppliers.actions.delete.confirmation.warning",
              {
                count: showWarningForRowKeys.length,
              }
            )}
            showIcon={false}
          />
        </Space>
      ),
    }
  );

  if (isEmpty(selectedRowKeys)) {
    return null;
  }

  return (
    <Button
      key="bulk-delete"
      icon={<DeleteIcon />}
      onClick={() => {
        confirmDelete();
      }}
    >
      {t("managesuppliers.actions.delete.button")}
    </Button>
  );
};
